import i18next from 'i18next';
import { all, takeEvery, call, fork, take, put, select } from 'redux-saga/effects';
import { requestPuppy, requestNapi, isApiError } from '@wix/da-http-client';
import { putErrorNotification } from '../../../utils/saga';
import { uploadAttachment, uploadAttachmentImmediately, uploadAttachmentProgress, uploadAttachmentSuccess, uploadAttachmentFailure, cancelAttachmentUpload, removeAttachment, repositionAttachment, setAttachmentCaption } from '../actions';
import { getExtendedDeviationById } from '../../deviations/selectors';
import { createUploadChannel } from './base';
import { uploadQueueWatcher } from './queue';
import { normalizeDeviation } from '../../normalizr/helpers';
import { putEntities } from '../../entities/actions';
function* createAttachmentUploadChannel(file, deviationId, attachmentType = 'pcp') {
    const url = 'deviation/attachments/add';
    const formData = new FormData();
    formData.append('attachment_file', file);
    formData.append('deviationid', `${deviationId}`);
    formData.append('type', attachmentType);
    return yield call(createUploadChannel, url, formData, 'attachment');
}
function* updateDeviationInTheStore(deviation) {
    const entities = normalizeDeviation(deviation);
    if (entities) {
        yield put(putEntities({
            entities
        }));
    }
}
function* uploadAttachmentHandler(action) {
    const { file, uploadId, parentDeviationId, type, onSuccess, onFailure } = action.payload;
    const channel = yield call(createAttachmentUploadChannel, file, parentDeviationId, type);
    // If a cancel action for this file id is fired we close the channel
    yield fork(function*() {
        yield take((action)=>action.type === cancelAttachmentUpload.toString() && action.payload.uploadId === uploadId);
        channel.close();
    });
    while(true){
        const { progress = 0, error, success, response } = yield take(channel);
        if (error) {
            yield put(uploadAttachmentFailure(uploadId, error));
            if (onFailure) {
                yield call(onFailure, {
                    uploadId
                }, error);
            }
            return;
        }
        if (success) {
            var _deviation_extended;
            const { fileId, deviationId, attachment } = response;
            // manually update deviation in the store
            const deviation = yield select(getExtendedDeviationById, deviationId);
            if (deviation === null || deviation === void 0 ? void 0 : (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.additionalMedia) {
                deviation.extended.additionalMedia = [
                    attachment,
                    ...deviation.extended.additionalMedia
                ];
                yield call(updateDeviationInTheStore, deviation);
            }
            yield put(uploadAttachmentSuccess(uploadId, fileId, deviationId, file));
            if (onSuccess) {
                yield call(onSuccess, {
                    uploadId,
                    fileId: fileId,
                    deviationId: deviationId,
                    file,
                    attachment
                });
            }
            return;
        }
        yield put(uploadAttachmentProgress(uploadId, progress));
    }
}
function* removeAttachmentHandler({ payload }) {
    var _deviation_extended;
    const { fileId, deviationId, type, onSuccess } = payload;
    const data = {
        fileid: fileId,
        deviationid: deviationId,
        type
    };
    const result = yield call(requestPuppy, {
        method: 'post',
        url: 'deviation/attachments/remove',
        data
    }, undefined);
    if (!result || isApiError(result) || !result.success) {
        yield putErrorNotification(i18next.t('somethingWentWrong'));
        return;
    }
    // Remove the attachment from the store
    const deviation = yield select(getExtendedDeviationById, deviationId);
    if (deviation === null || deviation === void 0 ? void 0 : (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.additionalMedia) {
        deviation.extended.additionalMedia = deviation.extended.additionalMedia.filter((a)=>a.fileId !== fileId);
        yield call(updateDeviationInTheStore, deviation);
    }
    onSuccess();
}
function* repositionAttachmentByFileId(attachments, fileId, newPosition) {
    const attachment = attachments.find((a)=>a.fileId === fileId);
    if (!attachment) {
        return;
    }
    const oldPosition = attachment === null || attachment === void 0 ? void 0 : attachment.position;
    if (newPosition > oldPosition) {
        attachments.forEach((a)=>{
            if (a.position > oldPosition && a.position <= newPosition) {
                a.position--;
            }
        });
        attachment.position = newPosition;
    } else if (newPosition < oldPosition) {
        attachments.forEach((a)=>{
            if (a.position < oldPosition && a.position >= newPosition) {
                a.position++;
            }
        });
        attachment.position = newPosition;
    }
    return attachments.sort((a, b)=>a.position - b.position);
}
function* repositionAttachmentHandler({ payload }) {
    var _deviation_extended;
    const { fileId, deviationId, position } = payload;
    // move attachment in the deviation immediately
    const deviation = yield select(getExtendedDeviationById, deviationId);
    if (!(deviation === null || deviation === void 0 ? void 0 : (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.additionalMedia)) {
        return;
    }
    const attachments = deviation.extended.additionalMedia;
    if (!attachments) {
        return;
    }
    const attachment = attachments.find((a)=>a.fileId === fileId);
    if (!attachment) {
        return;
    }
    // Reposition the attachment in the normalized store prior to sending the request, to get faster feedback
    const attachmentsBackup = JSON.stringify(attachments);
    deviation.extended.additionalMedia = yield call(repositionAttachmentByFileId, attachments, fileId, position);
    yield call(updateDeviationInTheStore, deviation);
    // Reposition on the server
    const data = {
        fileid: fileId,
        deviationid: deviationId,
        type: 'additional_image',
        position
    };
    const result = yield call(requestPuppy, {
        method: 'post',
        url: 'deviation/attachments/reposition',
        data
    }, undefined);
    if (!result || isApiError(result) || !result.success) {
        yield putErrorNotification(i18next.t('somethingWentWrong'));
        // undo repositioning
        deviation.extended.additionalMedia = JSON.parse(attachmentsBackup);
        yield call(updateDeviationInTheStore, deviation);
    }
}
function* setAttachmentCaptionHandler({ payload }) {
    var _deviation_extended;
    const { fileId, deviationId, caption } = payload;
    // move attachment in the deviation immediately
    const deviation = yield select(getExtendedDeviationById, deviationId);
    if (!(deviation === null || deviation === void 0 ? void 0 : (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.additionalMedia)) {
        return;
    }
    const attachments = deviation.extended.additionalMedia;
    if (!attachments) {
        return;
    }
    const attachment = attachments.find((a)=>a.fileId === fileId);
    if (!attachment) {
        return;
    }
    const data = {
        fileid: fileId,
        deviationid: deviationId,
        type: 'additional_image',
        editorRaw: JSON.stringify(caption)
    };
    // captions need node to transform editorRaw
    const result = yield call(requestNapi, {
        method: 'post',
        url: 'deviation/attachments/set_caption',
        data
    }, undefined);
    if (!result || isApiError(result) || !result.success) {
        yield putErrorNotification(i18next.t('somethingWentWrong'));
        return;
    }
    // update attachment in the store
    attachment.caption = caption;
    yield call(updateDeviationInTheStore, deviation);
}
export default function* saga() {
    yield all([
        takeEvery(uploadAttachmentImmediately, uploadAttachmentHandler),
        uploadQueueWatcher('attachment', [
            uploadAttachment,
            uploadAttachmentSuccess,
            uploadAttachmentFailure
        ], uploadAttachmentImmediately),
        takeEvery(removeAttachment, removeAttachmentHandler),
        takeEvery(repositionAttachment, repositionAttachmentHandler),
        takeEvery(setAttachmentCaption, setAttachmentCaptionHandler)
    ]);
}

//# sourceMappingURL=attachments.js.map